/**
 * Agenda ordering — "Any Other Business" (AOB) must ALWAYS be the last agenda item,
 * with every other item kept in its original (insertion) order.
 *
 * Numbering throughout the minutes pipeline is positional (number = index + 1), so
 * fixing the ORDER here auto-corrects the "ITEM N" numbering everywhere downstream.
 *
 * This module is intentionally dependency-free so the identical logic can be mirrored
 * on the backend (backend/.../contracts.js) without a shared-module boundary.
 */

// AOB preset id used by the new-UI agenda presets.
const AOB_ID = 'a_other';

// Title fallback for paths where the preset id is absent (e.g. backend / AI output).
// Anchored to the START of the string (after an optional "Item N:" prefix) so a
// legitimate custom item such as "Approval of acquisition of any other business unit"
// is NOT misclassified and silently sunk to the end.
const AOB_TITLE_RE = /^\s*(item\s*\d+\s*[:.)-]*\s*)?any other business\b/i;

export function isAobItem(item) {
  if (!item) return false;
  if (typeof item === 'string') return AOB_TITLE_RE.test(item);
  if (item.id === AOB_ID) return true;
  const text = item.label || item.title || '';
  return AOB_TITLE_RE.test(text);
}

/**
 * Stable two-bucket partition: non-AOB items keep their order, then AOB items last.
 * Stable by construction (single forward pass) — not reliant on Array.sort engine
 * stability. Non-arrays are returned untouched.
 */
export function orderAgendaItems(items) {
  if (!Array.isArray(items)) return items;
  const rest = [];
  const aob = [];
  for (const item of items) {
    (isAobItem(item) ? aob : rest).push(item);
  }
  return rest.concat(aob);
}
